import { NotificationStatus } from '@/__generated__/graphql';
import { useDispatch } from '@/store';
import TimeElapsed from '@components/TimeElapsed';
import TruncatedTypography from '@components/TruncatedTypography';
import { NotificationEntity } from '@features/notifications/graphql/queries/getNotifications';
import { getEntityAttributes } from '@features/notifications/utils/entitiesHandlers';
import { useLingui } from '@lingui/react';
import { Clear } from '@mui/icons-material';
import { Box, IconButton, ListItem, ListItemButton, Typography } from '@mui/material';
import { type FC } from 'react';

interface Props {
  notification: {
    id: number;
    status: NotificationStatus;
    notificationObject: {
      entity: NotificationEntity;
    };
    updatedAt: number;
  };
  handleDismissNotification: (notificationId: number) => void;
  handleViewNotification: (notificationId: number) => void;
}

const unreadNotificationStyle = {
  position: 'relative',
  '::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: 4,
    backgroundColor: '#1976d2',
  },
};

export const MenuListItem: FC<Props> = ({
  notification,
  handleDismissNotification,
  handleViewNotification,
}) => {
  const { i18n } = useLingui();
  const dispatch = useDispatch();
  const { title, subtitle, icon } = getEntityAttributes(
    notification.notificationObject.entity,
    dispatch,
    i18n,
  );
  return (
    <ListItem
      sx={{
        scrollSnapAlign: 'start',
        ...((notification.status === NotificationStatus.New ||
          notification.status === NotificationStatus.Unread) &&
          unreadNotificationStyle),
      }}
      disablePadding
      secondaryAction={
        <IconButton
          onClick={() => handleDismissNotification(notification.id)}
          size="small"
          edge="end"
          aria-label="dismiss"
        >
          <Clear fontSize="small" />
        </IconButton>
      }
    >
      <ListItemButton
        sx={{ display: 'block' }}
        onClick={() => handleViewNotification(notification.id)}
      >
        <Box display="flex" gap={1} alignItems="center" mb={0.2}>
          {icon}
          <TruncatedTypography fontSize="small" fontWeight="bold" flex={1}>
            {title}{' '}
          </TruncatedTypography>
          <Typography sx={{ fontSize: 12, lineHeight: 1 }} color="text.secondary">
            <TimeElapsed timestamp={notification.updatedAt} />
          </Typography>
        </Box>
        <TruncatedTypography display="block" fontSize="small">
          {subtitle}
        </TruncatedTypography>
      </ListItemButton>
    </ListItem>
  );
};
